import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "../../../util/user";
import Noti from "../../../util/noti";
import MaterialTable from "material-table";
import LoaderGraph from "../../general/LoaderGraph";
import moment from "moment-timezone";
import GYAxios from "../../../util/GYAxios";
import { checkIfArray } from "./../../../util/checking";
import dividePages from "./../../../util/dividePages";
import Select from "react-select";
import DatePicker from "react-datepicker";

export const OperatingTable = () => {
	const siteLicense = useUser().roles.includes("administration:SiteCreation:_license");
	const userEmail = useUser().email;
	const [ready, setReady] = useState(false); // table is ready or not
	const [data, setData] = useState([]); // data for table
	const [opCols, setOpCols] = useState([]);
	const [selectedRow, setSelectedRow] = useState(null);
	const [noOfPages, setNoOfPages] = useState([1]);

	const selectStyle = {
		container: () => ({ width: "80%", }),
		menu: () => ({ zIndex: 3, }),
	};

	async function fetchData(uri) {
		return await GYAxios.get(uri).then((res) => {
			try {
				if (res.data.error !== undefined) {
					console.log(res.data.error);
					return null;
				} else {
					if (checkIfArray(res.data)) { return res.data; } else {
						console.log(res.data.error);
						return null;
					}
				}
			} catch (e) {
				console.log(e);
				return null;
			}
		}).catch((err) => {
			console.log(err);
			Noti.create({ type: "e", msg: "Process is timeout.", });
			return null;
		});
	}

	function assignData(tempData) {
		setData(tempData);
		setReady(true);
	}

	function assignPages(length) {
		const pagesArray = dividePages(length, 30);
		setNoOfPages(pagesArray);
	}

	const getTableData = useCallback((uri) => {
		setData([]);

		fetchData(uri).then(function (fetched_sites) {
			if (fetched_sites === null) {
				assignData([]);
				Noti.create({
					type: "e",
					msg: "No site with operation status is fetched.",
				});
				return;
			}

			const tempArray = fetched_sites.map((v) => formatted_value(v));

			assignPages(tempArray.length);
			assignData(tempArray);
		});
	});

	function reload() {
		getTableData("/site_creation/created_sites");
	}

	const formatted_value = (v) => {
		const today = moment();
		const cod = moment(v.cod);
		const cod_greater_90 = cod.diff(today, "days") > 90;
		const msa_greater_90 = v.msa === null ? true : moment(v.msa).diff(today, "days") > 90;
		if (v.operation_status !== 3 && cod_greater_90) v.cod = null;
		if (v.operation_status !== 2 && msa_greater_90) v.msa = null;

		return {
			...v,
			cod: v.cod === null ? null : moment(v.cod).format("YYYY-MM-DD"),
			msa: v.msa === null ? null : moment(v.msa).format("YYYY-MM-DD"),
		};
	};

	const postingRequest = useCallback(async (uri, param = null) => {
		return await GYAxios.post(uri, param)
			.then((res) => {
				try {
					if (res.data.error !== undefined) {
						console.log(res.data.error);
						Noti.create({ type: "e", msg: "Something is wrong.", });
						return null;
					} else {
						return res.data;
					}
				} catch (e) {
					console.log(e);
					Noti.create({ type: "e", msg: "Something is wrong.", });
					return null;
				}
			}).catch((err) => {
				console.log(err);
				Noti.create({ type: "e", msg: "Something is wrong.", });
				return null;
			});
	}, []);

	function formatDateNoSpace(date) {
		return (date === null || date === "-") ? null : moment(date).format("YYYYMMDD");
	}

	function formatDateWithDash(date) {
		return (date === null || date === "-") ? null : moment(date, "YYYYMMDD").format("YYYY-MM-DD");
	}

	async function updateRow(newData, oldData) {
		let noti_type = "e";
		let noti_msg = "";
		let uri = `/site_creation/post/sites/${newData.table_name}?status=${newData.operation_status}&old_status=${oldData.operation_status}&email=${userEmail}`;
		if (newData.operation_status === oldData.operation_status) {
			if (newData.cod !== oldData.cod || newData.msa !== oldData.msa) { }
			else {
				Noti.create({ type: "w", msg: "No changes detected." });
				return;
			}
		}

		const est_cod = moment(oldData.estimated_cod).add(10, "y").startOf("year").format("YYYYMMDD");
		const est_msa = formatDateNoSpace(oldData.msa);
		const new_cod = formatDateNoSpace(newData.cod);
		const new_msa = formatDateNoSpace(newData.msa);

		if (newData.operation_status === 1 || newData.operation_status === 4) {
			if (newData.cod !== null || newData.msa !== null) {
				noti_type = "w";
				noti_msg = "Updating status and clearing new COD and/ or MSA...";
			}
			newData.cod = formatDateWithDash(est_cod);
			newData.msa = null;
			uri += `&cod=${est_cod}&msa=${newData.msa}`; 
		} else if (newData.operation_status === 2) {
			if (newData.cod !== null) {
				noti_type = "w";
				noti_msg = "Updating status and clearing new COD...";
			}
			
			newData.cod = formatDateWithDash(est_cod);
			newData.msa = formatDateWithDash(new_msa);
			uri += `&cod=${est_cod}&msa=${new_msa}`; // update all daily and monthly to operation_status since MSA date 
		} else if (newData.operation_status === 3) {
			// if status of site is COD then just update the status and COD
			newData.cod = formatDateWithDash(new_cod);
			newData.msa = formatDateWithDash(new_msa);
			uri += `&cod=${new_cod}&msa=${new_msa}`;
		}

		if (noti_msg !== "") Noti.create({ type: noti_type, msg: noti_msg });
		else Noti.create({ type: "i", msg: "Updating status..." });

		const result = await postingRequest(uri, {created_site_id: oldData.created_site_id, client_id: oldData.client_id,});
		if (result === null) Noti.create({ type: "e", msg: "Something is wrong." });
		else {
			const index = oldData.tableData.id;
			const dataUpdate = [...data];
			dataUpdate[index] = formatted_value(newData);
			setData([...dataUpdate]);
			const timeout = 1000;
			setTimeout(() => { 
				console.log(result);
				Noti.create({ type: "s", msg: "Updated." });
			}, timeout);
		}
	}

	useEffect(() => {
		getTableData("/site_creation/created_sites");
	}, []);

	useEffect(() => {
		if (data.length !== 0) {
			fetchData("/select/operation_status").then(function (fetched_status_list) {
				if (fetched_status_list === null) {
					Noti.create({ type: "e", msg: "No Operation Status has been fetched.", });
					return;
				}

				setOpCols([
					{
						title: "Country of Site",
						field: "country",
						headerStyle: { display: "none", width: "0px", maxWidth: "0px" },
						cellStyle: { display: "none", width: "0px", maxWidth: "0px" },
						editable: "never",
					},
					{ title: "Table Name", field: "table_name", editable: "never" },
					{
						title: "Operation Status",
						field: "operation_status",
						editComponent: ({ value, onChange }) => {
							const fetchedStatus = fetched_status_list.filter((v) => v.value === value)[0];
							return (
								<Select
									defaultValue={fetchedStatus}
									styles={selectStyle}
									options={fetched_status_list}
									name="statusSelect"
									onChange={(selectedOption) => onChange(selectedOption.value)}
									value={value ? value.value : value}
								/>
							);
						},
						render: (rowData) =>
							fetched_status_list.filter((v) => v.value === parseInt(rowData.operation_status))[0]?.label,
					},
					{
						title: "Estimated COD",
						field: "estimated_cod",
						editable: "never",
						render: (rowData) =>
							moment(rowData.estimated_cod).format("YYYY-MM-DD"),
					},
					{
						title: "COD Date (actual)",
						field: "cod",
						editComponent: (rowData) => {
							const wholeRowData = rowData.rowData;
							const updatedOperationStatus = wholeRowData.operation_status;
							return (
								<div>
									<DatePicker
										selected={rowData.value ? new Date(rowData.value) : null}
										onChange={(date) => rowData.onChange(date)}
										dateFormat={"yyyy-MM-dd"}
										onKeyDown={(e) => { e.preventDefault(); }}
										disabled={updatedOperationStatus === 3 ? false : true}
									/>
									{rowData.isValid ? null : (
										<p style={{ color: "red" }}>{rowData.helperText}</p>
									)}
								</div>
							);
						},
						render: (rowData) => (rowData.cod === null ? "-" : rowData.cod),
						validate: (rowData) => {
							if (
								rowData.operation_status === 3 &&
								(rowData.cod === null || rowData.cod === "-")
							) {
								return { isValid: false, helperText: "COD must be filled" };
							} else if (rowData.msa !== null && rowData.msa !== "-" && rowData.cod !== null && rowData.cod !== "-" && rowData.operation_status === 3) {
								if (moment(rowData.cod) > moment(rowData.msa)) return true;
								else {
									return {
										isValid: false,
										helperText: "COD must be greater than MSA Date",
									};
								}
							} else return true;
						},
					},
					{
						title: "MSA Date (actual)",
						field: "msa",
						type: "date",
						editComponent: (rowData) => {
							const wholeRowData = rowData.rowData;
							const updatedOperationStatus = wholeRowData.operation_status;
							return (
								<div>
									<DatePicker
										selected={rowData.value ? new Date(rowData.value) : null}
										onChange={(date) => rowData.onChange(date)}
										dateFormat={"yyyy-MM-dd"}
										onKeyDown={(e) => { e.preventDefault(); }}
										disabled={updatedOperationStatus === 2 ? false : true}
									/>
									{rowData.isValid ? null : (
										<p style={{ color: "red" }}>{rowData.helperText}</p>
									)}
								</div>
							);
						},
						render: (rowData) => (rowData.msa === null ? "-" : rowData.msa),
						validate: (rowData) => {
							if (rowData.operation_status === 2 && rowData.msa === null) {
								return {
									isValid: false,
									helperText: "MSA Date must be filled",
								};
							}
							else return true;
						},
					},
				]);
			});
		}
	}, [data]);

	return siteLicense && opCols.length !== 0 ? (
		<>
			<MaterialTable
				title={
					<div>
						<h5>
							Operation Status Control Panel{" "}
							<span
								className="	glyphicon glyphicon-refresh"
								onClick={() => reload()}
							></span>
						</h5>
					</div>
				}
				columns={opCols}
				data={data}
				isLoading={!ready}
				onRowClick={(evt, selectedRow) =>
					setSelectedRow(selectedRow.tableData.id)
				}
				editable={{
					onRowUpdate: async (newData, oldData) =>
						new Promise(async (resolve, reject) => {
							try {
								await updateRow(newData, oldData);
								resolve();
							} catch (e) {
								console.log(e);
								Noti.create({ type: "e", msg: "Something is wrong.", });
								reject();
							}
						}),
				}}
				options={{
					actionsColumnIndex: -1,
					sorting: true,
					pageSize: noOfPages[0]?.value ? noOfPages[0]?.value : noOfPages[0],
					pageSizeOptions: noOfPages,
					emptyRowsWhenPaging: false,
					rowStyle: (rowData) => ({
						backgroundColor:
							selectedRow === rowData.tableData.id ? "#F5DEB3" : "#FFF",
					}),
				}}
				style={{ marginBottom: "10px" }}
				localization={{ header: { actions: "Edit" } }}
			/>
		</>
	) : (
		<LoaderGraph />
	);
};
